import { events, type EventsChannel } from 'aws-amplify/data';
import { IRemoteAvatar, PlayerInfo } from './apis/avatar';
import { extractGameIdentifier, getAuthToken, getNamespace, publishEvent } from '../utils/amplify-config';
import axios from 'axios';

const API_BASE_URL = process.env.API_ENDPOINT;

type SubscriptionPayload = {
    id: string;
    type: 'data';
    event: PlayerInfo;
};

/**
 * Amplify Events implementation of IRemoteAvatar
 */
class AmplifyAvatar implements IRemoteAvatar {
    private channels: Map<string, EventsChannel> = new Map();
    private subscriptions: Map<string, any> = new Map();

    private async getOrCreateChannel(gameId: string): Promise<EventsChannel> {
        if (this.channels.has(gameId)) {
            console.log('👤 [AvatarEvents] Channel already exists for:', gameId);
            return this.channels.get(gameId)!;
        }

        const gameIdentifier = extractGameIdentifier(gameId);
        const channelName = `${getNamespace()}/game/${gameIdentifier}/avatars`;
        console.log('👤 [AvatarEvents] Connecting to avatar channel:', channelName);

        const channel = await events.connect(channelName, { authToken: getAuthToken() });
        console.log('✅ [AvatarEvents] Avatar channel connected successfully');

        this.channels.set(gameId, channel);
        return channel;
    }

    /**
     * Subscribe to avatar updates
     */
    onAvatarSnapshot(gameId: string, cb: (doc: PlayerInfo) => void) {
        console.log('👤 [AvatarEvents] Setting up avatar subscription for game:', gameId);
        this.unsubscribe(gameId);

        const promise = this.getOrCreateChannel(gameId)
            .then((channel) => {
                // If cancelled while connecting, don't subscribe
                if (!this.subscriptions.has(gameId)) {
                    console.warn('⚠️ [AvatarEvents] Subscription cancelled before channel connected');
                    return;
                }

                console.log('👤 [AvatarEvents] Creating avatar subscription...');
                const subscription = channel.subscribe({
                    next: ({ event: playerInfo }: SubscriptionPayload) => {
                        console.log('👤 [AvatarEvents] ✅ Received avatar event!', playerInfo);
                        cb(initPlayerInfo(playerInfo));
                    },
                    error: (error: Error) => {
                        console.error(`❌ [AvatarEvents] Error in avatar channel ${gameId}:`, error);
                    },
                });

                console.log('👤 [AvatarEvents] Avatar subscription created successfully');
                this.subscriptions.set(gameId, subscription);
            })
            .catch((error) => {
                console.error(`❌ [AvatarEvents] Failed to create avatar channel ${gameId}:`, error);
                this.subscriptions.delete(gameId);
            });

        // Store promise to indicate "connecting"
        this.subscriptions.set(gameId, promise);

        return () => this.unsubscribe(gameId);
    }

    private unsubscribe(key: string) {
        if (this.subscriptions.has(key)) {
            const sub = this.subscriptions.get(key);
            // If it's a subscription object
            if (sub && typeof sub.unsubscribe === 'function') {
                sub.unsubscribe();
            }
            // If it's a pending promise, we just remove it from the map.
            // The .then() block checks the map before subscribing, so it will respect this removal.
            this.subscriptions.delete(key);
        }
    }

    /**
     * Save avatar (REST API + AppSync Events)
     */
    async saveAvatar(
        gameId: string,
        playerId: string,
        avatar: string
    ): Promise<void> {
        // 1. Execute REST API call
        await axios.post(`${API_BASE_URL}/games/${gameId}/avatars`, {
            playerId,
            avatar,
        });

        // 2. Publish event to AppSync for real-time updates
        const gameIdentifier = extractGameIdentifier(gameId);

        await publishEvent(`/game/${gameIdentifier}/avatars`, {
            action: 'avatar-saved',
            gameId,
            playerId,
            avatar,
        });
    }

    /**
     * Clean up all channels and subscriptions
     */
    cleanup(): void {
        this.subscriptions.forEach((sub) => {
            if (sub && typeof sub.unsubscribe === 'function') {
                sub.unsubscribe();
            }
        });
        this.subscriptions.clear();

        this.channels.forEach((channel) => {
            channel.close();
        });
        this.channels.clear();
    }
}

const initPlayerInfo = (plain: Partial<PlayerInfo>): PlayerInfo => ({
    playerId: '',
    avatar: '',
    ...plain,
});

export const avatar = new AmplifyAvatar();